import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Form, InputGroup, Row, Col, Badge, Dropdown, Modal, Spinner } from 'react-bootstrap';
import { FaSearch, FaEye, FaFilter, FaFileInvoice } from 'react-icons/fa';
import Invoice from '../../components/Invoice';
import axiosInstance from '../../utils/axiosConfig';
import { toast } from 'react-toastify';

const Orders = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axiosInstance.get('/orders/all');
      setOrders(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
      setLoading(false);
      toast.error('Failed to load orders');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: 'info',
      processing: 'primary',
      shipped: 'warning',
      delivered: 'success',
      cancelled: 'danger',
      refunded: 'secondary'
    };
    
    const displayText = {
      pending: 'Pending',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      refunded: 'Refunded'
    };
    
    return <Badge bg={statusMap[status] || 'secondary'}>{displayText[status]}</Badge>;
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axiosInstance.put(`/orders/${orderId}/status`, { status: newStatus });
      toast.success('Order status updated successfully');
      fetchOrders(); // Refresh the orders list
    } catch (err) {
      console.error('Error updating order status:', err);
      toast.error('Failed to update order status');
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    // You can implement a details modal or navigation to a details page here
  };

  const handleViewInvoice = (order) => {
    setSelectedOrder(order);
    setShowInvoiceModal(true);
  };

  const filteredOrders = orders.filter(order => {
    // Apply status filter
    if (filter !== 'all' && order.status !== filter) {
      return false;
    }
    
    // Apply search filter (customer name or order ID)
    return order.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
           order.id.toString().includes(searchTerm);
  });

  if (loading) {
    return (
      <Card className="shadow-sm mb-4">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Row className="align-items-center mb-4">
            <Col>
              <h4 className="card-title mb-0">Orders</h4>
            </Col>
            <Col xs="auto">
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" id="dropdown-filter">
                  <FaFilter className="me-2" />
                  {filter === 'all' ? 'All Orders' : `${filter.charAt(0).toUpperCase() + filter.slice(1)}`}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setFilter('all')}>All Orders</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilter('pending')}>Pending</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilter('processing')}>Processing</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilter('shipped')}>Shipped</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilter('delivered')}>Delivered</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilter('cancelled')}>Cancelled</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilter('refunded')}>Refunded</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col md={6}>
              <InputGroup>
                <Form.Control
                  placeholder="Search by customer or order ID..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <Button variant="outline-secondary">
                  <FaSearch />
                </Button>
              </InputGroup>
            </Col>
          </Row>
          
          <Table responsive hover>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td>#{order.id}</td>
                  <td>{order.user_name}</td>
                  <td>{new Date(order.created_at).toLocaleDateString()}</td>
                  <td>{order.items?.length || 0}</td>
                  <td>₹{parseFloat(order.total_amount).toFixed(2)}</td>
                  <td>{getStatusBadge(order.status)}</td>
                  <td>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      className="me-2"
                      onClick={() => handleViewDetails(order)}
                    >
                      <FaEye />
                    </Button>
                    <Button 
                      variant="outline-info" 
                      size="sm" 
                      className="me-2"
                      onClick={() => handleViewInvoice(order)}
                    >
                      <FaFileInvoice />
                    </Button>
                    <Dropdown className="d-inline">
                      <Dropdown.Toggle variant="outline-secondary" size="sm" id={`dropdown-${order.id}`}>
                        Update Status
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleStatusChange(order.id, 'pending')}>Pending</Dropdown.Item>
                        <Dropdown.Item onClick={() => handleStatusChange(order.id, 'processing')}>Processing</Dropdown.Item>
                        <Dropdown.Item onClick={() => handleStatusChange(order.id, 'shipped')}>Shipped</Dropdown.Item>
                        <Dropdown.Item onClick={() => handleStatusChange(order.id, 'delivered')}>Delivered</Dropdown.Item>
                        <Dropdown.Item onClick={() => handleStatusChange(order.id, 'cancelled')}>Cancelled</Dropdown.Item>
                        <Dropdown.Item onClick={() => handleStatusChange(order.id, 'refunded')}>Refunded</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
              {filteredOrders.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <p className="text-muted mb-0">
                      {searchTerm || filter !== 'all' ? 'No orders found matching your criteria.' : 'No orders found.'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Invoice Modal */}
      <Modal 
        show={showInvoiceModal} 
        onHide={() => setShowInvoiceModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Invoice #{selectedOrder?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <Invoice 
              order={selectedOrder}
              items={selectedOrder.items}
              customerDetails={{
                name: selectedOrder.user_name,
                email: selectedOrder.user_email,
                address: selectedOrder.shipping_address
              }}
              showActions={false}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInvoiceModal(false)}>
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              const printWindow = window.open('', '_blank');
              
              const invoiceContent = document.getElementById('invoice-content').innerHTML;
              
              printWindow.document.write(`
                <html>
                  <head>
                    <title>Invoice #${selectedOrder?.id}</title>
                    <link 
                      href="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css" 
                      rel="stylesheet" 
                      integrity="sha384-1BmE4kWBq78iYhFldvKuhfTAU6auU8tT94WrHftjDbrCEXSU1oBoqyl2QvZ6jIW3" 
                      crossorigin="anonymous"
                    >
                    <style>
                      body { padding: 20px; }
                      .invoice-header { margin-bottom: 30px; }
                      @media print {
                        .no-print { display: none !important; }
                        body { padding: 0; }
                        .container { width: 100%; max-width: 100%; }
                      }
                    </style>
                  </head>
                  <body>
                    <div class="container">
                      ${invoiceContent}
                    </div>
                    <script>
                      window.onload = function() { window.print(); }
                    </script>
                  </body>
                </html>
              `);
              
              printWindow.document.close();
            }}
          >
            Download / Print
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Orders; 